import { useTranslations } from 'next-intl'

import { PropertyType as PropertyTypeEnum, RoomIcon } from '@/entities/room'
import { cn, ExpandButton, useExpand } from '@/shared/lib'

import { usePropertyTypes } from './option'
import { FilterType } from '../../../model/filter'
import { Title, List, Item, useFilterLayout } from '../../common/layout'

type Props = {
  type: FilterType
  defaultExpanded: boolean
  ref: (element: HTMLDivElement) => void
  className?: string
  selected: (value: PropertyTypeEnum) => boolean
  onExpand: () => void
  onChange: (value: PropertyTypeEnum) => void
}

const PROPERTY_TYPE_LIMIT = {
  vertical: 4,
  mobile: 4,
  horizontal: 6,
}

export const PropertyTypesInDialogView = ({
  type,
  defaultExpanded,
  ref,
  className,
  selected,
  onExpand,
  onChange,
}: Props) => {
  const t = useTranslations('filter')
  const layout = useFilterLayout()
  const isHorizontal = layout === 'horizontal'

  const { expanded, toggleExpand } = useExpand({
    defaultExpanded,
  })

  const { PROPERTY_TYPES } = usePropertyTypes()

  const propertyTypeList = expanded
    ? PROPERTY_TYPES
    : PROPERTY_TYPES.slice(0, PROPERTY_TYPE_LIMIT[layout])

  const handleExpandButtonClick = () => {
    toggleExpand()
    onExpand()
  }

  const handlePropertyTypeChange = (value: PropertyTypeEnum) => () => {
    onChange(value)
  }

  const listContent = (
    <>
      <List
        className={cn(
          'grid gap-2',
          isHorizontal ? 'grid-cols-6' : 'grid-cols-4',
        )}
      >
        {propertyTypeList.map(({ title, value, icon }) => (
          <Item
            key={title}
            type={type}
            size="large"
            selected={selected(value)}
            onClick={handlePropertyTypeChange(value)}
            className="w-full! whitespace-nowrap"
          >
            {type === FilterType.Dialog && (
              <RoomIcon
                name={icon}
                width={50}
                height={38}
                className="mx-auto mb-1"
              />
            )}
            <p
              className={cn(
                'flex h-11 items-center justify-center',
                type === FilterType.Dialog && 'whitespace-break-spaces',
              )}
            >
              {title}
            </p>
          </Item>
        ))}
      </List>

      {type === FilterType.Dialog && (
        <div className="mt-4 flex justify-center">
          <ExpandButton expanded={expanded} onClick={handleExpandButtonClick} />
        </div>
      )}
    </>
  )

  return (
    <div className={className} ref={ref}>
      <Title>{t('property_type.title')}</Title>
      {layout === 'horizontal' ? (
        <div className="min-w-0 flex-1">{listContent}</div>
      ) : (
        listContent
      )}
    </div>
  )
}
